import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Heart, Trash2, MessageCircle, Star, ChevronLeft, ChevronRight, Crown } from "lucide-react";
import {
  GiAries, GiTaurus, GiGemini, GiCancer, GiLeo, GiVirgo,
  GiLibra, GiScorpio, GiSagittarius, GiCapricorn, GiAquarius, GiPisces,
} from "react-icons/gi";
import type { IconType } from "react-icons";

import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { apiClient } from "@/api/ApiClient";
import { translationKeyLastSegment } from "@/lib/utils";
import type { ZodiacKey } from "@/api/models";

const ZODIAC_ICONS: Record<ZodiacKey, IconType> = {
  aries: GiAries,
  taurus: GiTaurus,
  gemini: GiGemini,
  cancer: GiCancer,
  leo: GiLeo,
  virgo: GiVirgo,
  libra: GiLibra,
  scorpio: GiScorpio,
  sagittarius: GiSagittarius,
  capricorn: GiCapricorn,
  aquarius: GiAquarius,
  pisces: GiPisces,
};

import romanticAvatar from "@/assets/avatar-romantic.jpg";
import badboyAvatar from "@/assets/avatar-badboy.jpg";
import intellectualAvatar from "@/assets/avatar-intellectual.jpg";
import protectorAvatar from "@/assets/avatar-protector.jpg";
import funnyAvatar from "@/assets/avatar-funny.jpg";

import realisticRomantic from "@/assets/realistic-romantic.jpg";
import realisticBadboy from "@/assets/realistic-badboy.jpg";
import realisticIntellectual from "@/assets/realistic-intellectual.jpg";
import realisticProtector from "@/assets/realistic-protector.jpg";
import realisticFunny from "@/assets/realistic-funny.jpg";
import sportyAvatar from "@/assets/avatar-sporty.jpg";

const animeAvatarMap = {
  romantic: romanticAvatar,
  badboy: badboyAvatar,
  intellectual: intellectualAvatar,
  protector: protectorAvatar,
  funny: funnyAvatar,
  sporty: sportyAvatar,
};

const realisticAvatarMap = {
  romantic: realisticRomantic,
  badboy: realisticBadboy,
  intellectual: realisticIntellectual,
  protector: realisticProtector,
  funny: realisticFunny,
  sporty: sportyAvatar, // Using anime version for now
};

interface FavoritesProps {
  onSelectBoyfriend: (boyfriend: any) => void;
  onNavigateToChat: (boyfriendId?: string) => void;
  onNavigateToProfile: () => void;
  onNavigateToSettings: () => void;
  onCreateNew: () => void;
  onNavigateToExplore?: () => void;
  onNavigateToPremium?: () => void;
  onNavigateToBoyfriendProfile?: (boyfriendId: string) => void;
  onToggleFavorite?: (boyfriendId: string) => void;
  user?: {
    id: string;
    email?: string;
    name?: string;
    isPremium?: boolean;
  } | null;
  onPromptLogin?: () => void;
  onTokensClick?: () => void;
  userTokens?: number;
  // no local favorites
}

export const Favorites = ({ 
  onSelectBoyfriend, 
  onNavigateToChat, 
  onNavigateToProfile, 
  onNavigateToSettings, 
  onCreateNew,
  onNavigateToExplore,
  onNavigateToPremium,
  onNavigateToBoyfriendProfile,
  onToggleFavorite,
  user, 
  onPromptLogin,
  onTokensClick,
  userTokens
}: FavoritesProps) => {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentFavoritePage, setCurrentFavoritePage] = useState(0);
  const itemsPerPage = 6;
  const { toast } = useToast();

  // Load favorites from database (no local storage)
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user) {
        setFavorites([]);
        setIsLoading(false);
        return;
      }

      // For authenticated users (including local users), use API
      try {
        console.log('Loading favorites from API for user:', user.id);
        const favData = await apiClient.get('/favorites');
        console.log('Loaded favorites from API:', favData);

        if (Array.isArray(favData)) {
          // Backend /favorites returns an incorrect avatarUrl; merge canonical avatar from /boyfriends by id.
          let merged = favData;
          try {
            const boyfriends = await apiClient.get('/boyfriends');
            if (Array.isArray(boyfriends)) {
              const byId = new Map(boyfriends.map((b: any) => [b.id, b.avatarUrl]));
              merged = favData.map((f: any) => ({ ...f, avatarUrl: byId.get(f.id) ?? f.avatarUrl }));
            }
          } catch (e) {
            console.warn('Could not merge canonical avatars from /boyfriends', e);
          }
          setFavorites(merged);
        } else {
          setFavorites([]);
        }
      } catch (error) {
        console.error('Error loading favorites from API:', error);
        setFavorites([]);
      }

      setIsLoading(false);
    };

    fetchFavorites();
  }, [user?.id]);

  const handleRemoveFromFavorites = async (boyfriendId: string) => {
    try {
      await apiClient.delete(`/favorites/${boyfriendId}`);
      
      // Update local state after successful API operation
      setFavorites(prev => prev.filter(fav => fav.id !== boyfriendId));
      
      // Also update global favorites state
      if (onToggleFavorite) {
        await onToggleFavorite(boyfriendId);
      }
      
      toast({
        title: "Removed from favorites",
        description: "Boyfriend removed from your favorites successfully.",
      });
    } catch (error) {
      console.error('Error removing from favorites:', error);
    }
  };

  // Navigation handlers for pagination
  const handlePrevFavorites = () => {
    setCurrentFavoritePage(prev => Math.max(0, prev - 1));
  };

  const handleNextFavorites = () => {
    const maxPage = Math.ceil(favorites.length / itemsPerPage) - 1;
    setCurrentFavoritePage(prev => Math.min(maxPage, prev + 1));
  };

  // Get current items for display
  const currentFavorites = favorites.slice(
    currentFavoritePage * itemsPerPage,
    (currentFavoritePage + 1) * itemsPerPage
  );

  const canNavigatePrevFavorites = currentFavoritePage > 0;
  const canNavigateNextFavorites = (currentFavoritePage + 1) * itemsPerPage < favorites.length;

  // Avatar URLs used directly from server

  if (isLoading) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="px-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {/* Header Skeleton */}
            <div className="mb-8">
              <div className="h-8 bg-muted/50 rounded w-48 mb-2 animate-pulse"></div>
              <div className="h-5 bg-muted/50 rounded w-64 animate-pulse"></div>
            </div>

            {/* Favorites Section Skeleton */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="h-7 bg-muted/50 rounded w-32 animate-pulse"></div>
                <div className="h-6 bg-muted/50 rounded w-24 animate-pulse"></div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-card/60 backdrop-blur-sm rounded-xl border border-primary/20 p-6">
                    <div className="text-center mb-4">
                      <div className="h-24 w-24 bg-muted/50 rounded-full mx-auto mb-4 animate-pulse"></div>
                      <div className="h-6 bg-muted/50 rounded w-24 mx-auto mb-2 animate-pulse"></div>
                      <div className="h-4 bg-muted/50 rounded w-16 mx-auto animate-pulse"></div>
                    </div>
                    <div className="space-y-3">
                      <div className="h-10 bg-muted/50 rounded animate-pulse"></div>
                      <div className="h-8 bg-muted/50 rounded animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
            {t('favorites.title')}
          </h1>
          {!user?.isPremium && (
            <Button variant="romantic" size="sm" onClick={onNavigateToPremium}>
              <Crown className="h-4 w-4 mr-2" />
              Upgrade
            </Button>
          )}
        </div>
      </div>
      
      <div className="px-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto">

          {/* Show empty state for unsigned users */}
          {!user && favorites.length === 0 && (
            <div className="text-center py-16">
              <Heart className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold text-muted-foreground mb-2">
                {t('favorites.no_favorites')}
              </h2>
              <p className="text-muted-foreground mb-4">
                {t('favorites.no_favorites_desc')}
              </p>
              <Button variant="romantic" onClick={onNavigateToExplore}>
                <Heart className="h-4 w-4 mr-2" />
                {t('favorites.explore')}
              </Button>
            </div>
          )}

          {/* Show empty state for signed users */}
          {user && favorites.length === 0 && (
            <div className="text-center py-16 relative">
              {/* Floating hearts animation */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
                  <Heart className="h-4 w-4 text-primary/30" />
                </div>
                <div className="absolute top-1/3 right-1/3 animate-bounce" style={{ animationDelay: '1s', animationDuration: '2.5s' }}>
                  <Heart className="h-3 w-3 text-primary/20" />
                </div>
                <div className="absolute bottom-1/3 left-1/2 animate-bounce" style={{ animationDelay: '2s', animationDuration: '2.8s' }}>
                  <Heart className="h-5 w-5 text-primary/25" />
                </div>
                <div className="absolute top-1/2 right-1/4 animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '3.2s' }}>
                  <Heart className="h-3 w-3 text-primary/15" />
                </div>
              </div>
              
              {/* Main content with enhanced styling */}
              <div className="relative z-10 animate-fade-in">
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full p-8 w-32 h-32 mx-auto mb-6 animate-pulse">
                  <Heart className="h-16 w-16 mx-auto text-primary opacity-60" />
                </div>
                
                <h2 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent mb-3">
                  {t('favorites.no_favorites')}
                </h2>
                
                <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                  {t('favorites.no_favorites_desc')}
                </p>
                
                <Button variant="romantic" onClick={onNavigateToExplore} className="hover-scale">
                  <Heart className="h-4 w-4 mr-2" />
                  {t('favorites.explore')}
                </Button>
              </div>
            </div>
          )}

          {/* Favorites Grid */}
          {favorites.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                
                {favorites.length > itemsPerPage && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handlePrevFavorites}
                      disabled={!canNavigatePrevFavorites}
                      className="h-8 w-8 text-muted-foreground hover:text-primary disabled:opacity-30"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {currentFavoritePage + 1} / {Math.ceil(favorites.length / itemsPerPage)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleNextFavorites}
                      disabled={!canNavigateNextFavorites}
                      className="h-8 w-8 text-muted-foreground hover:text-primary disabled:opacity-30"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {currentFavorites.map((favorite, index) => {
                  const ZodiacIcon = favorite.zodiacKey ? ZODIAC_ICONS[favorite.zodiacKey] : null;
                  const zodiacLabel = favorite.zodiac;
                  const occupation = (favorite as any).occupation as string | undefined;
                  const location = favorite.location || 'Virtual World';
                  return (
                  <Card
                    key={`${favorite.id}-${index}`}
                    className="group cursor-pointer hover:scale-105 transition-all duration-300 border-primary/20 hover:border-primary/40 animate-fade-in overflow-hidden relative h-64"
                    onClick={() => {
                      onNavigateToBoyfriendProfile?.(favorite.id);
                    }}
                  >
                    {/* Background Image */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundImage: `url(${favorite.avatarUrl})` }}
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Remove from Favorites Button */}
                    <div className="absolute top-3 right-3 z-10">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={(e) => e.stopPropagation()}
                            className="h-10 w-10 border border-white/30 backdrop-blur-sm transition-all duration-300 text-red-500 hover:text-red-600 bg-red-500/20 hover:bg-red-500/30 ring-2 ring-red-500/50 border-red-500/50"
                          >
                            <Heart 
                              className="h-5 w-5" 
                              fill="currentColor"
                            />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove from Favorites</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to remove {favorite.name} from your favorites?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFromFavorites(favorite.id);
                              }}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    
                    {/* Content Overlay */}
                    <div className="absolute inset-0 p-4 flex flex-col justify-between text-white">
                      <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 self-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium text-white/90 drop-shadow-lg">Online</span>
                      </div>
                      <div className="pb-10">
                        <h3 className="font-bold text-xl mb-1 drop-shadow-lg">{favorite.name}{favorite.age ? <span className="font-normal text-base">, {favorite.age}</span> : null}</h3>
                        {zodiacLabel && (
                          <p className="text-sm font-medium mb-2 text-primary-glow drop-shadow-lg flex items-center gap-1">
                            {ZodiacIcon && <ZodiacIcon className="h-3.5 w-3.5 opacity-70" />}
                            {zodiacLabel}
                          </p>
                        )}
                        <p className="text-xs text-white/80 mb-1">{occupation ? `${occupation} • ${location}` : location}</p>
                      </div>
                    </div>
                    
                    {/* Chat Button - Bottom Right */}
                    <div className="absolute bottom-3 right-3">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="bg-pink-500/20 hover:bg-gradient-to-r hover:from-pink-500 hover:to-purple-500 text-white border border-pink-400/40 hover:border-pink-300 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 hover:scale-125 text-xs hover:shadow-lg hover:shadow-pink-500/50"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Chat Now clicked for:', favorite.name, 'ID:', favorite.id);
                          console.log('Favorite data:', favorite);
                          // Create character object with proper structure
                          const rawArt = favorite.art_style as string | undefined;
                          const styleKey =
                            !rawArt || rawArt === 'realistic'
                              ? 'boyfriend_attr.style.realistic'
                              : rawArt === 'anime'
                                ? 'boyfriend_attr.style.anime'
                                : rawArt.startsWith('boyfriend_attr.')
                                  ? rawArt
                                  : `boyfriend_attr.style.${rawArt}`;
                          const characterData = {
                            id: favorite.id,
                            name: favorite.name,
                            avatar: translationKeyLastSegment(favorite.personality) || 'romantic',
                            personality: favorite.personality,
                            style: styleKey,
                            age: favorite.age || 25,
                            explore_id: favorite.id // Important for proper identification
                          };
                          console.log('Character data being set:', characterData);
                          onSelectBoyfriend(characterData);
                          onNavigateToChat(favorite.id);
                        }}
                      >
                        <MessageCircle className="h-3 w-3 mr-1" />
                        {t('boyfriend_profile.chat_now')}
                      </Button>
                    </div>
                  </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
